<template>
  <div>
    <CCard>
      <CCardHeader class="d-flex justify-content-between align-items-center">
        Global Settings
        <CButton
          color="primary"
          size="sm"
          @click="openCreateModal"
        >
          <CIcon
            size="sm"
            name="cil-plus"
          /> Add Setting
        </CButton>
      </CCardHeader>

      <CCardBody>
        <div
          v-if="isLoading"
          class="d-flex justify-content-center align-items-center py-5"
        >
          <CSpinner
            color="info"
            grow
          />
        </div>

        <CDataTable
          v-else
          small
          border
          sorter
          column-filter
          :items="items"
          :fields="fields"
          :sorter-value="{ column: 'lastUpdateDT', asc: false }"
          :no-items-view="{ noResults: 'No results matching filters', noItems: 'No global settings found' }"
        >
          <template #value="{item}">
            <td>
              <span v-if="item.typeName === 'Boolean'">
                {{ parseBool(item.value) ? 'true' : 'false' }}
              </span>
              <span v-else>{{ item.value }}</span>
            </td>
          </template>

          <template #isPublic="{item}">
            <td>{{ item.isPublic ? 'Yes' : 'No' }}</td>
          </template>

          <template #lastUpdateDT="{item}">
            <td>{{ formatTimestamp(item.lastUpdateDT) }}</td>
          </template>

          <template #actions="{item}">
            <td>
              <CButton
                size="sm"
                color="primary"
                variant="ghost"
                @click="openEditModal(item)"
              >
                Edit
              </CButton>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <CModal
      title="Edit Global Setting"
      color="primary"
      :show.sync="showEditModal"
    >
      <div v-if="editItem">
        <div class="form-group">
          <label>Setting ID</label>
          <p class="font-weight-bold mb-0">
            {{ editItem.settingId }}
          </p>
        </div>

        <div class="form-group">
          <label>Type</label>
          <p class="mb-0">
            {{ editItem.typeName }}
          </p>
        </div>

        <div class="form-group">
          <label for="edit-value">Value</label>
          <div v-if="editItem.typeName === 'Boolean'">
            <CSelect
              id="edit-value"
              :value="editValue"
              :options="[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]"
              @update:value="editValue = $event"
            />
          </div>
          <div v-else>
            <CInput
              id="edit-value"
              v-model="editValue"
              :type="editItem.typeName === 'Int32' || editItem.typeName === 'Decimal' ? 'number' : 'text'"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="edit-note">Note</label>
          <CInput
            id="edit-note"
            v-model="editNote"
            placeholder="Reason for change"
          />
        </div>

        <div class="form-group">
          <CInputCheckbox
            id="edit-is-public"
            label="Public (accessible without super-admin)"
            :checked="editIsPublic"
            @update:checked="editIsPublic = $event"
          />
        </div>
      </div>

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isSaving"
          :disabled="!editNote.trim() || !editValue"
          @click="saveEdit"
        >
          Save
        </ButtonLoading>
        <CButton
          color="secondary"
          @click="showEditModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <CModal
      title="Add Global Setting"
      color="primary"
      :show.sync="showCreateModal"
    >
      <div>
        <div class="form-group">
          <label for="create-id">Setting ID</label>
          <CInput
            id="create-id"
            v-model="createSettingId"
            placeholder="e.g. MyFeatureFlag"
          />
        </div>

        <div class="form-group">
          <label for="create-type">Type</label>
          <CSelect
            id="create-type"
            :value="createTypeName"
            :options="typeOptions"
            @update:value="createTypeName = $event"
          />
        </div>

        <div class="form-group">
          <label for="create-value">Value</label>
          <div v-if="createTypeName === 'Boolean'">
            <CSelect
              id="create-value"
              :value="createValue"
              :options="[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]"
              @update:value="createValue = $event"
            />
          </div>
          <div v-else>
            <CInput
              id="create-value"
              v-model="createValue"
              :type="createTypeName === 'Int32' || createTypeName === 'Decimal' ? 'number' : 'text'"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="create-note">Note</label>
          <CInput
            id="create-note"
            v-model="createNote"
            placeholder="Reason for adding this setting"
          />
        </div>

        <div class="form-group">
          <CInputCheckbox
            id="create-is-public"
            label="Public (accessible without super-admin)"
            :checked="createIsPublic"
            @update:checked="createIsPublic = $event"
          />
        </div>
      </div>

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isSaving"
          :disabled="!createSettingId.trim() || !createNote.trim() || !createValue"
          @click="saveCreate"
        >
          Add Setting
        </ButtonLoading>
        <CButton
          color="secondary"
          @click="showCreateModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <CModal
      title="Please login again to verify it's you"
      color="primary"
      :show.sync="showLoginModal"
    >
      <LoginInternal
        v-if="showLoginModal"
        @success="onLoginSuccess"
        @error="onLoginFailed"
      />

      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
  </div>
</template>

<script>
import GlobalSettingsAPI from '@/api/global-settings.api'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { formatDateTime } from '@/utils'
import ButtonLoading from '@/components/common/ButtonLoading.vue'
import LoginInternal from '@/views/Login/LoginInternal.vue'

const TYPE_MAP = {
  Boolean: 'bool',
  Int32: 'int',
  Decimal: 'decimal',
  String: 'string'
}

export default {
  name: 'GlobalSettings',

  title: 'Global Settings',

  components: {
    ButtonLoading,
    LoginInternal
  },

  data() {
    return {
      isLoading: false,
      isSaving: false,
      showLoginModal: false,
      pendingAction: null,
      showEditModal: false,
      editItem: null,
      editValue: '',
      editNote: '',
      editIsPublic: false,
      showCreateModal: false,
      createSettingId: '',
      createTypeName: 'Boolean',
      createValue: 'false',
      createNote: '',
      createIsPublic: false,
      typeOptions: [
        { value: 'Boolean', label: 'Boolean' },
        { value: 'Int32', label: 'Int32' },
        { value: 'Decimal', label: 'Decimal' },
        { value: 'String', label: 'String' }
      ],
      items: [],
      fields: [
        { key: 'settingId', label: 'Setting ID', sorter: true },
        { key: 'typeName', label: 'Type', sorter: true },
        { key: 'value', label: 'Value', sorter: true, filter: false },
        { key: 'isPublic', label: 'Public', sorter: true, filter: false },
        { key: 'lastUpdateDT', label: 'Last Updated', sorter: true, filter: false },
        { key: 'lastUpdateByUserID', label: 'Updated By User ID', sorter: true },
        { key: 'actions', label: '', sorter: false, filter: false }
      ]
    }
  },

  computed: {
    isUserLoggedInWithinLast5Minutes() {
      return this.$store.getters['auth/isUserLoggedInWithinLastXMinutes'](5)
    }
  },

  watch: {
    createTypeName(newType) {
      if (newType === 'Boolean') {
        this.createValue = 'false'
      } else {
        this.createValue = ''
      }
    }
  },

  async created() {
    await this.loadData()
  },

  methods: {
    async loadData() {
      this.isLoading = true
      try {
        const response = await GlobalSettingsAPI.getAllSettings()
        this.items = response.data
      } catch {
        showErrorMessage('Failed to load global settings')
      } finally {
        this.isLoading = false
      }
    },

    openEditModal(item) {
      this.editItem = item
      this.editNote = ''
      this.editIsPublic = item.isPublic

      if (item.typeName === 'Boolean') {
        this.editValue = this.parseBool(item.value) ? 'true' : 'false'
      } else {
        this.editValue = item.value
      }

      this.showEditModal = true
    },

    async saveEdit() {
      if (!this.editNote.trim()) {
        return
      }

      if (!this.isUserLoggedInWithinLast5Minutes) {
        this.pendingAction = 'edit'
        this.showLoginModal = true
        return
      }

      await this._doSaveEdit()
    },

    async _doSaveEdit() {
      const type = TYPE_MAP[this.editItem.typeName]
      if (!type) {
        showErrorMessage(`Unsupported setting type: ${this.editItem.typeName}`)
        return
      }

      let value = this.editValue
      if (this.editItem.typeName === 'Boolean') {
        value = this.editValue === 'true'
      } else if (this.editItem.typeName === 'Int32') {
        value = parseInt(this.editValue, 10)
      } else if (this.editItem.typeName === 'Decimal') {
        value = parseFloat(this.editValue)
      }

      this.isSaving = true
      try {
        await GlobalSettingsAPI.setSettingValue({
          type,
          settingId: this.editItem.settingId,
          value,
          note: this.editNote,
          isPublic: this.editIsPublic
        })
        showSuccessMessage(`Setting "${this.editItem.settingId}" updated successfully`)
        this.showEditModal = false
        await this.loadData()
      } catch {
        showErrorMessage(`Failed to update setting "${this.editItem.settingId}"`)
      } finally {
        this.isSaving = false
      }
    },

    openCreateModal() {
      this.createSettingId = ''
      this.createTypeName = 'Boolean'
      this.createValue = 'false'
      this.createNote = ''
      this.createIsPublic = false
      this.showCreateModal = true
    },

    async saveCreate() {
      if (!this.createSettingId.trim() || !this.createNote.trim() || !this.createValue) {
        return
      }

      if (!this.isUserLoggedInWithinLast5Minutes) {
        this.pendingAction = 'create'
        this.showLoginModal = true
        return
      }

      await this._doSaveCreate()
    },

    async _doSaveCreate() {
      const type = TYPE_MAP[this.createTypeName]
      let value = this.createValue
      if (this.createTypeName === 'Boolean') {
        value = this.createValue === 'true'
      } else if (this.createTypeName === 'Int32') {
        value = parseInt(this.createValue, 10)
      } else if (this.createTypeName === 'Decimal') {
        value = parseFloat(this.createValue)
      }

      this.isSaving = true
      try {
        await GlobalSettingsAPI.setSettingValue({
          type,
          settingId: this.createSettingId,
          value,
          note: this.createNote,
          isPublic: this.createIsPublic
        })
        showSuccessMessage(`Setting "${this.createSettingId}" created successfully`)
        this.showCreateModal = false
        await this.loadData()
      } catch {
        showErrorMessage(`Failed to create setting "${this.createSettingId}"`)
      } finally {
        this.isSaving = false
      }
    },

    async onLoginSuccess() {
      this.showLoginModal = false
      if (this.pendingAction === 'edit') {
        await this._doSaveEdit()
      } else if (this.pendingAction === 'create') {
        await this._doSaveCreate()
      }

      this.pendingAction = null
    },

    onLoginFailed() {
      this.$store.dispatch('auth/logout')
    },

    formatTimestamp(timestamp) {
      if (!timestamp) {
        return '-'
      }

      return formatDateTime(timestamp)
    },

    parseBool(value) {
      if (value === true) {
        return true
      }

      const normalized = String(value).trim().toLowerCase()
      return normalized === 'true' || normalized === '1'
    }
  }
}
</script>
